import React, { useState, useEffect } from 'react';

/**
 * Controls component provides interface for selecting video clip start/end times
 * 
 * Features:
 * - Time input fields for precise control
 * - Range slider for visual selection
 * - Real-time validation
 * - Clip button to process the video
 * - Progress indicator during processing
 */
const Controls = ({ 
  duration, 
  startTime, 
  endTime, 
  onStartTimeChange, 
  onEndTimeChange, 
  onClipVideo, 
  isProcessing 
}) => {
  const [localStartTime, setLocalStartTime] = useState(startTime);
  const [localEndTime, setLocalEndTime] = useState(endTime);
  const [validationError, setValidationError] = useState('');

  // Update local state when props change
  useEffect(() => {
    setLocalStartTime(startTime);
  }, [startTime]);

  useEffect(() => {
    setLocalEndTime(endTime);
  }, [endTime]);

  /**
   * Format time for display (MM:SS)
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Parse time input (MM:SS or seconds)
   */
  const parseTime = (timeString) => {
    if (!timeString) return 0;
    
    // Handle MM:SS format
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      return minutes * 60 + seconds;
    }
    
    // Handle seconds format
    return parseFloat(timeString) || 0;
  };

  /**
   * Validate time inputs
   */
  const validateTimes = (start, end) => {
    if (start < 0) return 'Start time cannot be negative';
    if (end > duration) return 'End time cannot exceed video duration';
    if (start >= end) return 'End time must be greater than start time';
    if (end - start < 1) return 'Clip must be at least 1 second long';
    return '';
  };

  /**
   * Handle start time change
   */
  const handleStartTimeChange = (value) => {
    const newStartTime = parseTime(value);
    setLocalStartTime(newStartTime);
    
    const error = validateTimes(newStartTime, localEndTime);
    setValidationError(error);
    
    if (!error) {
      onStartTimeChange(newStartTime);
    }
  };

  /**
   * Handle end time change
   */
  const handleEndTimeChange = (value) => {
    const newEndTime = parseTime(value);
    setLocalEndTime(newEndTime);
    
    const error = validateTimes(localStartTime, newEndTime);
    setValidationError(error);
    
    if (!error) {
      onEndTimeChange(newEndTime);
    }
  };

  /**
   * Handle slider change for start time
   */
  const handleStartSliderChange = (event) => {
    const newStartTime = parseFloat(event.target.value);
    setLocalStartTime(newStartTime);
    onStartTimeChange(newStartTime);
    setValidationError('');
  };

  /**
   * Handle slider change for end time
   */
  const handleEndSliderChange = (event) => {
    const newEndTime = parseFloat(event.target.value);
    setLocalEndTime(newEndTime);
    onEndTimeChange(newEndTime);
    setValidationError('');
  };

  /**
   * Handle clip button click
   */
  const handleClipClick = () => {
    const error = validateTimes(localStartTime, localEndTime);
    if (error) {
      setValidationError(error);
      return;
    }
    
    onClipVideo();
  };

  /**
   * Calculate clip duration
   */
  const clipDuration = localEndTime - localStartTime;

  return (
    <div className="controls-container">
      <h3>Select Clip Range</h3>
      
      {/* Time Input Fields */}
      <div className="controls-row">
        <div className="control-group">
          <label className="control-label">Start Time</label>
          <input
            type="text"
            className="time-input"
            value={formatTime(localStartTime)}
            onChange={(e) => handleStartTimeChange(e.target.value)}
            placeholder="MM:SS"
            disabled={isProcessing}
          />
        </div>
        
        <div className="control-group">
          <label className="control-label">End Time</label>
          <input
            type="text"
            className="time-input"
            value={formatTime(localEndTime)}
            onChange={(e) => handleEndTimeChange(e.target.value)}
            placeholder="MM:SS"
            disabled={isProcessing}
          />
        </div>
        
        <div className="control-group">
          <label className="control-label">Duration</label>
          <div className="time-display">
            {formatTime(clipDuration)}
          </div>
        </div>
      </div>

      {/* Range Sliders */}
      <div className="controls-row">
        <div className="control-group">
          <label className="control-label">Start: {formatTime(localStartTime)}</label>
          <input
            type="range"
            min="0"
            max={duration}
            step="0.1"
            value={localStartTime}
            onChange={handleStartSliderChange}
            className="time-slider"
            disabled={isProcessing}
          />
        </div>
      </div>
      
      <div className="controls-row">
        <div className="control-group">
          <label className="control-label">End: {formatTime(localEndTime)}</label>
          <input
            type="range"
            min="0"
            max={duration}
            step="0.1"
            value={localEndTime}
            onChange={handleEndSliderChange}
            className="time-slider"
            disabled={isProcessing}
          />
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="error-message">
          {validationError}
        </div>
      )}

      {/* Clip Button */}
      <div className="controls-row">
        <button
          className="clip-button"
          onClick={handleClipClick}
          disabled={isProcessing || !!validationError}
        >
          {isProcessing ? 'Processing...' : 'Clip Video'}
        </button>
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <div className="progress-text">
            Processing video... This may take a few moments depending on video size.
          </div>
        </div>
      )}

      {/* Video Info */}
      <div className="video-info">
        <p><strong>Total Duration:</strong> {formatTime(duration)}</p>
        <p><strong>Selected Range:</strong> {formatTime(localStartTime)} - {formatTime(localEndTime)}</p>
        <p><strong>Clip Duration:</strong> {formatTime(clipDuration)}</p>
      </div>
    </div>
  );
};

export default Controls;

